import { cop, type Cantidad, type COP } from '../money.js';
import type { Descuadre } from '../tipos/dominio.js';

/**
 * Denominaciones del peso colombiano en circulación, de mayor a menor.
 *
 * El orden importa: la interfaz las presenta así porque es el orden en que se cuenta
 * una caja. Las monedas de 500, 200, 100 y 50 van al final, agrupadas aparte. La de 50 entró
 * el 2026-10-01: el dueño del negocio tenía 32 en la caja y no había dónde contarlas.
 */
export const DENOMINACIONES = [100_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100, 50] as const;

export type Denominacion = (typeof DENOMINACIONES)[number];

const VALIDAS = new Set<number>(DENOMINACIONES);

/**
 * Total del efectivo contado.
 *
 * El efectivo contado NUNCA es un total digitado: es la suma de denominación por
 * cantidad. Por eso el operario no teclea montos de siete cifras en la pantalla
 * crítica, teclea cantidades de billetes.
 */
export function totalConteo(conteo: Readonly<Record<number, Cantidad>>): COP {
  let total = 0;
  for (const [clave, cant] of Object.entries(conteo)) {
    const denominacion = Number(clave);
    if (!VALIDAS.has(denominacion)) {
      throw new RangeError(`${clave} no es una denominación válida del peso colombiano`);
    }
    // El tipo `Cantidad` lo borra el compilador, así que no protege nada en ejecución.
    // Este conteo llega de un formulario, cruza la red como JSON y vuelve deserializado
    // sin haber pasado por `cantidad()`: ahí un negativo o un fraccionario se multiplica
    // y se suma sin que nada se queje. Es el mismo motivo por el que todo monto que
    // cruza el borde pasa por `parseCOPdeAPI` en vez de confiar en el tipo `COP`.
    if (!Number.isSafeInteger(cant) || cant < 0) {
      throw new RangeError(
        `No se pueden contar ${String(cant)} billetes de ${clave}: la cantidad debe ser un entero no negativo`,
      );
    }
    total += denominacion * cant;
  }
  return cop(total);
}

/**
 * Clasifica la diferencia entre lo contado y lo esperado.
 *
 * SIEMPRE devuelve un descuadre físico, jamás aritmético: una diferencia entre el
 * dinero que hay y el que debería haber es legítima y se justifica con una nota. El
 * descuadre aritmético nace del desglose por medio de pago, no del conteo, y ese sí
 * bloquea. La nota arranca vacía y la llena el operario.
 */
export function clasificarDescuadre(contado: COP, esperado: COP): Descuadre {
  if (contado === esperado) return { tipo: 'ninguno' };
  const diferencia = cop(contado - esperado);
  return {
    tipo: 'fisico',
    diferencia,
    sentido: diferencia < 0 ? 'faltante' : 'sobrante',
    nota: '',
  };
}
